import { useEffect, useRef, useState } from 'react';
import type { RenderedPreview } from '../lib/types.js';
import { Pill } from './ui.js';

/**
 * One client preview, in a sandboxed iframe.
 *
 * The document contains user-authored HTML, and a signature has no business
 * running anything, so `allow-scripts` is deliberately absent -- nothing in the
 * frame executes. `allow-same-origin` is present only so the parent can read
 * `contentDocument` to measure the rendered height; with scripting off it grants
 * the content no capability of its own. Without it the frame gets an opaque
 * origin, `contentDocument` comes back null, every measurement silently bails
 * and every preview sits at its initial height with its own scrollbar -- which
 * is exactly what happened before.
 *
 * The frame is sized to the client's real reading-pane width and auto-heighted
 * to its content, so the comparison view shows genuine differences in wrapping
 * rather than differences in frame size.
 */
export function DevicePreview({
  preview,
  scale = 1,
}: {
  preview: RenderedPreview;
  scale?: number;
}): JSX.Element {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(180);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const measure = () => {
      // Guarded rather than assumed: a sandbox policy change, or a browser that
      // refuses the access, must not throw on every timer tick.
      let doc: Document | null = null;
      try {
        doc = frame.contentDocument;
      } catch {
        return;
      }
      if (!doc?.body) return;
      // scrollHeight of the documentElement, not the body: the body is padded
      // and a margin-collapsed body under-reports.
      const next = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
      if (next > 0) setHeight(next + 4);
    };

    frame.addEventListener('load', measure);
    // Images arrive after load and change the height.
    const timers = [60, 300, 900, 2000].map((ms) => window.setTimeout(measure, ms));

    return () => {
      frame.removeEventListener('load', measure);
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [preview.document]);

  const isPhone = preview.chrome === 'phone';

  return (
    <div className="device">
      <div className="device-bar">
        <span className="row">
          {preview.label}
          {preview.darkMode && <Pill tone="accent">dark</Pill>}
        </span>
        <span className="meta">{preview.viewportWidth}px</span>
      </div>

      <div className={isPhone ? 'device-frame phone' : 'device-frame'}>
        <iframe
          ref={frameRef}
          title={`${preview.label} preview`}
          sandbox="allow-same-origin"
          srcDoc={preview.document}
          style={{
            width: isPhone ? preview.viewportWidth : '100%',
            minWidth: isPhone ? preview.viewportWidth : undefined,
            height,
            transform: scale === 1 ? undefined : `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        />
      </div>

      {preview.notes.length > 0 && (
        <div className="device-notes">
          <ul>
            {preview.notes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      <details className="quirks">
        <summary>What this client does to your HTML</summary>
        <ul>
          {preview.quirks.map((quirk, i) => (
            <li key={i}>{quirk}</li>
          ))}
        </ul>
      </details>
    </div>
  );
}

/** Side-by-side comparison across clients. */
export function PreviewGrid({
  previews,
  columns,
}: {
  previews: RenderedPreview[];
  columns?: number;
}): JSX.Element {
  return (
    <div
      className="preview-grid"
      style={
        columns
          ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
          : undefined
      }
    >
      {previews.map((preview) => (
        <DevicePreview key={preview.clientId} preview={preview} />
      ))}
    </div>
  );
}

/**
 * Client picker for the comparison view. Enforces at least one selection, so
 * the preview area can never end up empty.
 */
export function ClientPicker({
  clients,
  selected,
  onChange,
}: {
  clients: Array<{ id: string; shortLabel: string; supportsDarkMode: boolean }>;
  selected: string[];
  onChange: (ids: string[]) => void;
}): JSX.Element {
  const toggle = (id: string) => {
    if (selected.includes(id)) {
      if (selected.length === 1) return;
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  return (
    <div className="row wrap">
      {clients.map((client) => {
        const on = selected.includes(client.id);
        return (
          <button
            key={client.id}
            type="button"
            className={on ? 'sm primary' : 'sm'}
            aria-pressed={on}
            onClick={() => toggle(client.id)}
            title={
              on && selected.length === 1
                ? 'At least one client must stay selected'
                : undefined
            }
          >
            {client.shortLabel}
          </button>
        );
      })}
    </div>
  );
}
